import React from 'react'
import PropTypes from 'prop-types'
import uuid from 'react-uuid'
import { Grid, SvgIcon, Typography, useMediaQuery } from '@mui/material'
import { Box } from '@mui/system'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'
import { useParams } from 'react-router-dom'

import { getCurrencySymbol } from '@hapana/shared/utils/string.util'
import exclamation from '@hapana/shared/web/styles/assets/svgs/exclamation-circle-small.svg'
import exclamationTriangle from '@hapana/shared/web/styles/assets/svgs/exclamation-triangle.svg'
import Button from '@hapana/shared/web/components/shared/button/button.component'
import { BUTTON_VARIANTS } from '@hapana/shared/web/components/shared/button/button.constants'
import { BUTTON_SIZE } from '@hapana/shared/web/components/shared/button/button.constants'
import { BUTTON_THEME } from '@hapana/shared/web/components/shared/button/button.constants'
import Accordion from '@hapana/shared/web/components/shared/accordion/accordion.component'
import { useSiteConfiguration } from '@hapana/shared/web/hooks/siteConfiguration.hooks'
import { PAYMENT_TYPE } from '@hapana/shared/web/components/shared/individual-alerts/individual-alerts.constants'

import { styles } from './payments-list.styles'

const PaymentsList = ({ payments, outlined, type }) => {
  const { t } = useTranslation(['checkIn', 'common'])
  const { data: configuration } = useSiteConfiguration()
  const matchesDesktop = useMediaQuery((theme) => theme.breakpoints.up('sm'))
  const { siteId } = useParams()

  const currentTimezone = configuration?.timezone
  const siteCurrency = configuration?.currency

  const getRetryUrl = (invoiceId) =>
    `/s/${siteId}/retail/checkout?mode=retry&invoiceId=${invoiceId}`

  const getPayUrl = (invoiceId) => `/s/${siteId}/retail/checkout?mode=pay&invoiceId=${invoiceId}`

  const renderButtonLabel = () => {
    if (type === PAYMENT_TYPE.FAILED) {
      return t(matchesDesktop ? 'checkIn:retryPayment' : 'checkIn:retry')
    }
    return t(matchesDesktop ? 'checkIn:completePayment' : 'common:complete')
  }

  if (payments.length === 1) {
    const paymentUrl =
      type === PAYMENT_TYPE.FAILED
        ? getRetryUrl(payments[0].invoiceId)
        : getPayUrl(payments[0].invoiceId)

    return (
      <Box sx={styles.wrapper}>
        <Box sx={styles.warningWrapper}>
          <Box sx={type === PAYMENT_TYPE.FAILED ? styles.iconHolder : styles.iconHolderOutstanding}>
            <SvgIcon
              component={type === PAYMENT_TYPE.FAILED ? exclamation : exclamationTriangle}
              sx={styles.exclamationIcon}
            />
          </Box>
          <Typography typography="header4" color="gray.100">
            {t(
              type === PAYMENT_TYPE.FAILED ? 'checkIn:failedPayment' : 'checkIn:outstandingPayment',
              { count: payments.length }
            )}
            {matchesDesktop ? '  ' : <br />}
            {payments[0].datetime && (
              <Typography component="span" typography="medium" color="gray.64">
                {t('common:on')}{' '}
                {format(
                  utcToZonedTime(new Date(payments[0].datetime), currentTimezone),
                  'MMM dd, yyyy'
                )}
              </Typography>
            )}
          </Typography>
        </Box>
        <Box sx={styles.buttonWrapper}>
          <Button
            sx={styles.linkButton}
            variant={BUTTON_VARIANTS.OUTLINED}
            size={BUTTON_SIZE.SMALL}
            theme={BUTTON_THEME.GRAY}
            href={paymentUrl}
          >
            {renderButtonLabel()}
          </Button>
        </Box>
      </Box>
    )
  }

  return (
    <Grid container>
      <Accordion
        expandLabel={t(matchesDesktop ? 'checkIn:viewPayments' : 'checkIn:view')}
        id={uuid()}
        content={payments.map((payment) => {
          const paymentUrl =
            type === PAYMENT_TYPE.FAILED
              ? getRetryUrl(payment.invoiceId)
              : getPayUrl(payment.invoiceId)

          return (
            <Box sx={styles.multiplePaymentItemWrapper} key={payment.invoiceId}>
              <Box sx={styles.multiplePaymentItem}>
                <Typography typography="header4" color="gray.80">
                  {payment.label}
                </Typography>
                <Typography sx={styles.valueLocationItem} typography="medium" color="gray.80">
                  {[
                    payment.currency || siteCurrency
                      ? `${getCurrencySymbol(payment.currency || siteCurrency)}${payment.price}`
                      : payment.price,
                    payment.siteName,
                  ]
                    .filter(Boolean)
                    .join(' • ')}
                </Typography>
                {payment.datetime && (
                  <Typography typography="medium" color="gray.80">
                    {format(
                      utcToZonedTime(new Date(payment.datetime), currentTimezone),
                      'MMM dd, yyyy'
                    )}
                  </Typography>
                )}
              </Box>
              <Box sx={styles.retryButtonWrapper}>
                <Button
                  sx={styles.linkButton}
                  variant={BUTTON_VARIANTS.OUTLINED}
                  size={BUTTON_SIZE.SMALL}
                  theme={BUTTON_THEME.GRAY}
                  href={paymentUrl}
                >
                  {renderButtonLabel()}
                </Button>
              </Box>
            </Box>
          )
        })}
        groupedRows
        label={
          <Box sx={styles.warningWrapper}>
            <Box
              sx={
                type === PAYMENT_TYPE.FAILED
                  ? styles.accordionIconHolder
                  : styles.accordionIconHolderOutstanding
              }
            >
              <SvgIcon
                component={type === PAYMENT_TYPE.FAILED ? exclamation : exclamationTriangle}
                sx={styles.exclamationIcon}
              />
            </Box>
            <Typography typography="header4" color="gray.100">
              {t(
                type === PAYMENT_TYPE.FAILED
                  ? 'checkIn:failedPayment'
                  : 'checkIn:outstandingPayment',
                { count: payments.length }
              )}
            </Typography>
          </Box>
        }
        summaryStyles={styles.accordionSummary}
        contentStyles={styles.accordionContent}
      />
    </Grid>
  )
}

PaymentsList.propTypes = {
  outlined: PropTypes.bool,
  payments: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      currency: PropTypes.string,
      siteName: PropTypes.string,
      datetime: PropTypes.string,
      invoiceId: PropTypes.string,
    })
  ),
  type: PropTypes.oneOf(Object.values(PAYMENT_TYPE)),
}

PaymentsList.defaultProps = {
  outlined: false,
  paymentsList: [],
}

export default PaymentsList
